import { useEffect, useRef, useState } from "react";
import ScopeToggle from "./ScopeToggle";
import StandingCard, { standingVisible } from "./StandingCard";
import SubjectRadar, { radarVisible } from "./SubjectRadar";
import ScoreTrend, { trendVisible } from "./ScoreTrend";
import BandBreakdown, { bandsVisible } from "./BandBreakdown";
import { styles } from "./styles";

// Parent island for the cohort-comparison charts. Fetches the anonymous
// aggregate payload per scope (re-fetching on the scope toggle). Cards only
// appear once their cohort is large enough — we never show "not enough data"
// placeholders. The whole section stays hidden until there's something to
// compare against.
//
// Trap-free toggle: the "my sitting" cohort is always a SUBSET of "all CRE", so
// if "all" has nothing to show, "sitting" can't either — meaning we only ever
// hide the toggle entirely on the default "all" scope, never stranding a user
// who toggled into an empty "sitting" view.
export default function PerformanceCharts({ endpoint, sitting_label }) {
  const [scope, setScope] = useState("all");
  const [state, setState] = useState({ status: "loading", data: null });
  const reqId = useRef(0);

  useEffect(() => {
    let active = true;
    const myReq = ++reqId.current;
    setState((s) => ({ ...s, status: "loading" }));

    (async () => {
      try {
        const res = await fetch(`${endpoint}?scope=${scope}`, {
          headers: { Accept: "application/json" },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (active && myReq === reqId.current) setState({ status: "ready", data });
      } catch {
        if (active && myReq === reqId.current) setState({ status: "error", data: null });
      }
    })();

    return () => {
      active = false;
    };
  }, [endpoint, scope]);

  const { status, data } = state;

  // Stay quiet (and header-less) until we know there's something to show, so we
  // never flash a "How you compare" heading that then disappears.
  if (status === "loading") {
    return (
      <div style={styles.shell}>
        <p style={styles.loading}>
          <i className="fa-solid fa-circle-notch fa-spin" style={{ marginRight: 8 }} />
          Loading your comparison…
        </p>
      </div>
    );
  }

  if (status === "error" || !data) {
    return (
      <div style={styles.shell}>
        <p style={styles.empty}>Couldn't load your comparison just now. Refresh to try again.</p>
      </div>
    );
  }

  const show = {
    percentile: standingVisible(data.percentile),
    radar: radarVisible(data.radar),
    trend: trendVisible(data.trend),
    bands: bandsVisible(data.bands),
  };
  const anyVisible = Object.values(show).some(Boolean);

  // Nothing to compare on the default scope → hide the section completely.
  // (Subset property guarantees the "sitting" cohort is also empty here.)
  if (!anyVisible && scope === "all") return null;

  const sittingLabel = data.sitting_label ?? sitting_label ?? null;
  const sittingAvailable = data.sitting_available ?? false;
  const scopeCaption = scope === "sitting" && sittingLabel
    ? `candidates sitting ${sittingLabel}`
    : "candidates preparing for the CRE";

  return (
    <div style={styles.shell}>
      <div style={styles.header}>
        <div>
          <div style={styles.eyebrow}>
            <i className="fa-solid fa-users" style={{ marginRight: 8 }} />
            How you compare
          </div>
          <p style={styles.subtitle}>Anonymous, aggregate standing against others preparing for the CRE.</p>
        </div>
        <ScopeToggle
          scope={scope}
          sittingAvailable={sittingAvailable}
          sittingLabel={sittingLabel}
          onChange={setScope}
        />
      </div>

      {anyVisible ? (
        <div style={styles.grid}>
          {show.percentile && <StandingCard data={data.percentile} scopeLabel={scopeCaption} />}
          {show.radar && <SubjectRadar data={data.radar} />}
          {show.trend && <ScoreTrend data={data.trend} />}
          {show.bands && <BandBreakdown data={data.bands} />}
        </div>
      ) : (
        // Reachable only on the "sitting" scope (the user toggled in): one
        // neutral line instead of a wall of "not enough data" cards.
        <p style={styles.empty}>
          Your{sittingLabel ? ` ${sittingLabel}` : ""} sitting cohort isn't large enough to compare yet —
          switch to <strong>All CRE candidates</strong> to see where you stand.
        </p>
      )}
    </div>
  );
}
